import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";
import { FormikErrors } from "formik";

import useTheme from "../../../services/theme-service/ThemeService";
import { translationService } from "../../../services/translation/TranslationService";
import { DownloadInspectionFilesState } from "../../../services/inspection/DownloadInspectionFilesModel";
import DownloadButton from "../../../components/buttons/download-button/DownloadButton";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { ButtonProps } from "../../../components/button/Button";
import { InspectionFilesStorage } from "../../../services/inspection/InspectionFilesStorage";
import {
  DownloadInspectionFiles,
  DownloadInspectionFilesFromAssignedProject,
} from "../../../services/authorization/Permissions";
import { Dialog } from "../../../components/dialog/Dialog";
import { Dropdown, DropdownOption } from "../../../components/dropdown/Dropdown";
import FormField from "../../../components/form-field/FormField";

interface DownloadMultipleInspectionFilesModalProps {
  inspectionFilesStorage: InspectionFilesStorage;
  downloadInspectionFilesState: DownloadInspectionFilesState;
}

const DownloadMultipleInspectionFilesModal = observer(
  (props: DownloadMultipleInspectionFilesModalProps) => {
    const { downloadInspectionFilesState, inspectionFilesStorage } = { ...props };

    const { themeClass } = useTheme();

    const handleDownloadMultipleFiles = useCallback(async () => {
      const archiveName = downloadInspectionFilesState.archiveName;
      const selectedFiles = [...inspectionFilesStorage.selectedItems];
      inspectionFilesStorage.deselectAll();

      downloadInspectionFilesState.startDownload();
      await inspectionFilesStorage.downloadInspectionFiles(selectedFiles, archiveName);
    }, [downloadInspectionFilesState, inspectionFilesStorage]);

    return (
      <Dialog
        isVisible={downloadInspectionFilesState.archiveNameSetupModalIsVisible}
        sidePosition
        onHide={downloadInspectionFilesState.hideArchiveNameSetupModal}
        titleKey="inspectionFiles.DownloadMultipleFilesModal.title"
        buttonOk={{
          textKey: "general.download",
          action: handleDownloadMultipleFiles,
          disabled: !downloadInspectionFilesState.archiveName,
        }}
        buttonCancel={{
          textKey: "general.cancel",
          action: downloadInspectionFilesState.hideArchiveNameSetupModal,
        }}
      >
        <div className="field">
          <label className={themeClass(["label-edit"])}>{`${translationService.translate(
            "inspectionFiles.DownloadMultipleFilesModal.instruction"
          )} *`}</label>
          <input
            className="input"
            value={downloadInspectionFilesState.archiveName}
            onChange={(e) => downloadInspectionFilesState.setArchiveName(e.target.value)}
          />
        </div>
      </Dialog>
    );
  }
);

interface DownloadInspectionFilesButtonProps extends ButtonProps {
  inspectionFilesStorage: InspectionFilesStorage;
  downloadInspectionFilesState: DownloadInspectionFilesState;
}

export const DownloadInspectionFilesButton = observer(
  (props: DownloadInspectionFilesButtonProps) => {
    const { inspectionFilesStorage, downloadInspectionFilesState } = { ...props };

    const isDisabled = useCallback(
      () =>
        !inspectionFilesStorage.anyItemsSelected ||
        !permissionsService.userHasOneOfThePermissions([
          DownloadInspectionFiles,
          DownloadInspectionFilesFromAssignedProject,
        ]),
      [inspectionFilesStorage.anyItemsSelected]
    );

    const handleDownloadFiles = useCallback(async () => {
      if (inspectionFilesStorage.moreThanOneItemsSelected) {
        downloadInspectionFilesState.showArchiveNameSetupModal();
      } else {
        const fileId = inspectionFilesStorage.selectedItems[0];
        const fileName = inspectionFilesStorage.getFileName(fileId);
        await inspectionFilesStorage.downloadInspectionFile(fileId, fileName);
      }
    }, [downloadInspectionFilesState, inspectionFilesStorage]);

    return (
      <>
        <DownloadButton disabled={isDisabled()} onClick={handleDownloadFiles} />
        <DownloadMultipleInspectionFilesModal
          inspectionFilesStorage={inspectionFilesStorage}
          downloadInspectionFilesState={downloadInspectionFilesState}
        />
      </>
    );
  }
);

interface EditInspectionRelevanceProps {
  isRelevant: boolean;
  setFieldValue: (field: string, value: any) => Promise<void | FormikErrors<any>>;
}

export const EditInspectionRelevance = observer((props: EditInspectionRelevanceProps) => {
  const { isRelevant, setFieldValue } = props;

  const optionsList: DropdownOption[] = [
    {
      id: true.toString(),
      name: translationService.translate("general.inspection.relevant"),
    },
    {
      id: false.toString(),
      name: translationService.translate("general.inspection.outdated"),
    },
  ];

  return (
    <FormField
      id="isRelevant"
      className="select"
      labelKey="inspections.DisplayInspectionRelevance.label"
      fieldName="isRelevant"
      fieldValue={isRelevant}
      errorName="isRelevant"
      children={() => (
        <Dropdown
          selectedOption={
            optionsList.find((o) => o.id === isRelevant.toString()) ?? { id: "", name: "" }
          }
          options={optionsList}
          fieldName="isRelevant"
          setFieldValue={(field: string, value: any) => {
            setFieldValue(field, value.id);
          }}
        />
      )}
    />
  );
});
